import React, { useState, useEffect } from 'react';
import Modal from '../Common/Modal';
import { couponService } from '../../api/couponService';
import { TrendingUp, Users, DollarSign, ShoppingCart } from 'lucide-react';
import '../Common/Common.css';

function CouponStatsModal({ isOpen, onClose, couponId, couponCode }) {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && couponId) {
      fetchStatistics();
    }
  }, [isOpen, couponId]);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await couponService.getCouponStatistics(couponId);
      setStatistics(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Statistiques - ${couponCode}`} size="large">
      {loading ? (
        <div className="loading">Chargement des statistiques...</div>
      ) : error ? (
        <div className="error-text">{error}</div>
      ) : statistics ? (
        <div>
          {/* Statistics Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#3182ce' }}>
                <TrendingUp size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Total utilisations</div>
                <div className="stat-value">{statistics.total_uses || 0}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#38a169' }}>
                <Users size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Utilisateurs uniques</div>
                <div className="stat-value">{statistics.unique_users || 0}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#e53e3e' }}>
                <DollarSign size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Remise totale</div>
                <div className="stat-value" style={{ fontSize: '20px' }}>{formatCurrency(statistics.total_discount)}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#805ad5' }}>
                <ShoppingCart size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Revenu généré</div>
                <div className="stat-value" style={{ fontSize: '20px' }}>{formatCurrency(statistics.total_revenue)}</div>
              </div>
            </div>
          </div>

          {/* Recent Usages Table */}
          {statistics.recent_usages && statistics.recent_usages.length > 0 ? (
            <div>
              <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>
                Utilisations récentes
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Utilisateur</th>
                      <th>Commande</th>
                      <th>Montant commande</th>
                      <th>Remise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.recent_usages.map((usage, index) => (
                      <tr key={index}>
                        <td>{formatDate(usage.created_at)}</td>
                        <td>
                          {usage.user_name || usage.user_email || 'N/A'}
                        </td>
                        <td>
                          {usage.order_id ? `#${usage.order_id}` : 'N/A'}
                        </td>
                        <td>{formatCurrency(usage.order_total)}</td>
                        <td>
                          <strong style={{ color: '#e53e3e' }}>
                            -{formatCurrency(usage.discount_amount)}
                          </strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="no-data">
              <p>Aucune utilisation enregistrée pour ce coupon</p>
            </div>
          )}

          {/* Usage Chart Placeholder */}
          {statistics.usage_by_date && statistics.usage_by_date.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>
                Utilisations par date
              </h3>
              <div style={{ padding: '20px', backgroundColor: '#f7fafc', borderRadius: '8px', textAlign: 'center' }}>
                <p style={{ color: '#718096', fontSize: '14px' }}>
                  Graphique d'utilisation disponible prochainement
                </p>
                <div style={{ marginTop: '12px', fontSize: '12px', color: '#a0aec0' }}>
                  {statistics.usage_by_date.map((item, index) => (
                    <div key={index} style={{ marginBottom: '4px' }}>
                      {item.date}: {item.count} utilisation{item.count > 1 ? 's' : ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="no-data">Aucune statistique disponible</div>
      )}
    </Modal>
  );
}

export default CouponStatsModal;
